import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { TFunction } from 'i18next';
import type { SpecCharter, SpecCharterInput } from '../../../lib/api/spec-requirements.js';

/**
 * 欄と見出しの翻訳キー。起票フォームと同じ順に並べる。
 * module scope では hook を呼べないので、見出しは鍵で持って描画時に `t()` で解く。
 * @graph-connects none
 */
export const CHARTER_FIELDS = [
  ['purpose', 'spec.charter.fields.purpose'],
  ['background', 'spec.charter.fields.background'],
  ['goal', 'spec.charter.fields.goal'],
  ['deliverables', 'spec.charter.fields.deliverables'],
  ['preconditions', 'spec.charter.fields.preconditions'],
  ['constraints', 'spec.charter.fields.constraints'],
  ['schedule', 'spec.charter.fields.schedule'],
  ['cost', 'spec.charter.fields.cost'],
  ['structure', 'spec.charter.fields.structure'],
  ['notes', 'spec.charter.fields.notes'],
] as const satisfies readonly (readonly [keyof SpecCharterInput, string])[];

export interface CharterEditorProps {
  /** 今保存されている憲章。無い案件では null */
  charter: SpecCharter | null;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 触った欄だけを保存する */
  onSave: (input: Partial<SpecCharterInput>) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/**
 * 保存できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.error.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.error.saveFailed');
}

/** 保存済みの値を編集用に取り出す。無ければ全欄を空文字にする。 @graph-connects none */
function toDraft(charter: SpecCharter | null): SpecCharterInput {
  const empty = Object.fromEntries(CHARTER_FIELDS.map(([key]) => [key, ''])) as SpecCharterInput;
  if (charter === null) return empty;
  return Object.fromEntries(CHARTER_FIELDS.map(([key]) => [key, charter[key]])) as SpecCharterInput;
}

/**
 * プロジェクト憲章を編集する。
 *
 * API は**渡した欄だけを上書きし、渡さなかった欄は今の値を残す**。触っていない欄まで送ると
 * 「同時に別の入口から直した欄」を巻き戻すので、**変わった欄だけを送る**。
 *
 * **ただし案件仕様書のタブから読んだ憲章は、まだ保存されていない。** 変わった欄だけを送ると、
 * 送らなかった欄（タブにあった目的・背景など）が空のまま保存され、以後は保存された方が正になって
 * タブの値が見えなくなる。そのときは空の憲章と比べて、値のある欄を全部送る（タブの値を写す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト憲章の 10 欄を編集し、変わった欄だけを保存する（案件仕様書から読んだ憲章は値のある欄を全部保存する）
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function CharterEditor({ charter, saving, error, onSave, onCancel }: CharterEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SpecCharterInput>(() => toDraft(charter));

  const changedOnly = (): Partial<SpecCharterInput> => {
    const saved = toDraft(charter?.source === 'specSheet' ? null : charter);
    return Object.fromEntries(
      CHARTER_FIELDS.filter(([key]) => draft[key] !== saved[key]).map(([key]) => [key, draft[key]]),
    );
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(changedOnly());
      }}
    >
      <dl className="space-y-3">
        {CHARTER_FIELDS.map(([key, labelKey]) => (
          <div key={key}>
            <label className="block">
              <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
                {t(labelKey)}
              </span>
              <textarea
                className="mt-1 block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
                rows={key === 'purpose' || key === 'background' ? 3 : 2}
                value={draft[key]}
                aria-label={t(labelKey)}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, [key]: e.target.value }));
                }}
              />
            </label>
          </div>
        ))}
      </dl>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {t(saving ? 'spec.action.saving' : 'spec.action.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.action.cancel')}
        </button>
      </div>
    </form>
  );
}
